import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import { commentService } from '../../services/commentService';

const CommentSection = ({ postSlug, allowComments = true }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (allowComments) {
            fetchComments();
        }
    }, [postSlug, sortOrder]);

    const fetchComments = async () => {
        try {
            setLoading(true);
            const data = await commentService.getComments(postSlug, {
                sort: sortOrder,
                per_page: 50
            });
            setComments(data.comments || []);
        } catch (err) {
            console.error('Error loading comments:', err);
            setError('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    const handleCommentSubmit = async (formData) => {
        try {
            setSubmitting(true);
            setError('');
            setSuccessMessage('');

            await commentService.submitComment(postSlug, formData);

            setSuccessMessage('Thank you! Your comment has been submitted and is awaiting moderation.');

            // Optionally refresh comments after 2 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);

        } catch (err) {
            console.error('Error submitting comment:', err);
            setError(err.response?.data?.message || 'Failed to submit comment. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplySubmit = async (replyData, parentId) => {
        try {
            setError('');
            await commentService.submitComment(postSlug, {
                ...replyData,
                parent_id: parentId
            });

            setSuccessMessage('Thank you! Your reply has been submitted and is awaiting moderation.');
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);

        } catch (err) {
            console.error('Error submitting reply:', err);
            setError(err.response?.data?.message || 'Failed to submit reply. Please try again.');
        }
    };

    if (!allowComments) {
        return null;
    }

    return (
        <div className="mt-16 border-t border-gray-200 pt-16">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-2">
                    <MessageCircle className="w-6 h-6" style={{ color: '#C9A14F' }} />
                    <h2 className="text-3xl font-bold" style={{ color: '#31415A' }}>
                        Comments ({comments.length})
                    </h2>
                </div>

                {comments.length > 0 && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Sort by:</span>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': '#C9A14F' }}
                        >
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
                    <p className="text-green-800">{successMessage}</p>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
                    <p className="text-red-800">{error}</p>
                </div>
            )}

            {/* Comments List */}
            {loading ? (
                <div className="text-center py-12">
                    <p className="text-gray-500">Loading comments...</p>
                </div>
            ) : comments.length > 0 ? (
                <div className="mb-12">
                    {comments.map((comment) => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            onReply={handleReplySubmit}
                            depth={0}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 mb-12">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No comments yet. Be the first to comment!</p>
                </div>
            )}

            {/* Comment Form */}
            <CommentForm onSubmit={handleCommentSubmit} isSubmitting={submitting} />
        </div>
    );
};

export default CommentSection;
